import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import type { LineItemResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import { ColumnVisibilityMenu, DataTable, RichTextPreview } from '@trellis/roots/data'
import { SearchInput } from '@trellis/roots/forms'
import { computeLineItemSubtotal } from '@trellis/shared'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useLineItemsQuery } from '@/hooks'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { usePinnedColumn } from '@/hooks/usePinnedColumn'
import { getCurrencyFormatter } from '@/lib/format'

import type { RegistryRowValue } from './LineItemRow'

const UNIT_TYPE_LABELS: Record<string, string> = {
  HOUR: '/hr',
  DAY: '/day',
  WEEK: '/wk',
  MONTH: '/mo',
  UNIT: '/unit',
  FLAT: 'flat',
}

function unitTypeLabel(unitType: string): string {
  return UNIT_TYPE_LABELS[unitType] ?? `/${unitType.toLowerCase()}`
}

const currencyFormatter = getCurrencyFormatter()

function formatDiscount(registryItem: LineItemResponseDto): string {
  if (registryItem.discountType === 'PERCENTAGE') return `${registryItem.discount}%`
  if (registryItem.discountType === 'FIXED') {
    return currencyFormatter.format(registryItem.discount)
  }
  return '—'
}

const COLUMN_VISIBILITY_STORAGE_KEY = 'registry-browser-columns'
const PINNED_COLUMN_STORAGE_KEY = 'registry-browser-pinned-column'
const DEFAULT_COLUMN_VISIBILITY = { discount: false }

const registryColumns: ColumnDef<LineItemResponseDto>[] = [
  {
    accessorKey: 'title',
    header: 'Title',
    meta: { truncate: true },
    cell: ({ row }) => (
      <div className="max-w-64">
        <span className="block truncate font-medium">{row.original.title}</span>
        <span className="text-muted-foreground block truncate text-xs tabular-nums">
          {currencyFormatter.format(row.original.unitPrice)}
          {unitTypeLabel(row.original.unitType)} · Qty {row.original.quantity}
        </span>
      </div>
    ),
  },
  {
    id: 'description',
    accessorFn: (registryItem) => registryItem.description ?? '',
    header: 'Description',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="w-72 whitespace-normal">
        <RichTextPreview
          content={row.original.description}
          lines={3}
          className="text-muted-foreground text-xs"
        />
      </div>
    ),
  },
  {
    id: 'discount',
    accessorFn: (registryItem) => registryItem.discount,
    header: 'Discount',
    meta: { align: 'right' },
    cell: ({ row }) => (
      <span className="text-muted-foreground tabular-nums">
        {formatDiscount(row.original)}
      </span>
    ),
  },
  {
    id: 'subtotal',
    accessorFn: (registryItem) =>
      computeLineItemSubtotal(
        registryItem.unitPrice,
        registryItem.quantity,
        registryItem.discount,
        registryItem.discountType,
      ),
    header: 'Subtotal',
    meta: { align: 'right' },
    cell: ({ getValue }) => (
      <span className="font-medium tabular-nums">
        {currencyFormatter.format(getValue<number>())}
      </span>
    ),
  },
]

// The Title column has no id: it is the sticky identity column and is never toggleable.
const COLUMN_SPECS = registryColumns.flatMap((column) =>
  column.id && typeof column.header === 'string'
    ? [{ id: column.id, label: column.header }]
    : [],
)

interface RegistryItemBrowserProps {
  onAdd: (items: RegistryRowValue[]) => void
  onClose: () => void
}

export function RegistryItemBrowser({ onAdd, onClose }: RegistryItemBrowserProps) {
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const { columnVisibility, onColumnVisibilityChange } = useColumnVisibility(
    COLUMN_VISIBILITY_STORAGE_KEY,
    DEFAULT_COLUMN_VISIBILITY,
  )
  const { pinnedColumnId, onPinnedColumnChange } = usePinnedColumn(
    PINNED_COLUMN_STORAGE_KEY,
  )
  // Selection is keyed by id and outlives searches, so a selected item can drop out of
  // the current results; this remembers every item seen so "Add selected" still finds it.
  const seenItemsById = useRef(new Map<string, LineItemResponseDto>())

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data, isLoading } = useLineItemsQuery({
    q: debouncedSearch || undefined,
    limit: 50,
  })
  const registryItems = useMemo(
    () => data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [data],
  )

  useEffect(() => {
    for (const registryItem of registryItems) {
      seenItemsById.current.set(registryItem.id, registryItem)
    }
  }, [registryItems])

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id])

  function handleRowClick(registryItem: LineItemResponseDto) {
    setRowSelection((previous) => {
      const { [registryItem.id]: wasSelected, ...rest } = previous
      return wasSelected ? rest : { ...rest, [registryItem.id]: true }
    })
  }

  function handleAdd() {
    const selectedItems = selectedIds
      .map((id) => seenItemsById.current.get(id))
      .filter((registryItem) => registryItem !== undefined)
      .map(
        (registryItem): RegistryRowValue => ({
          type: 'registry',
          lineItemId: registryItem.id,
          title: registryItem.title,
          unitPrice: registryItem.unitPrice,
          unitType: registryItem.unitType,
          description: registryItem.description,
          taxable: registryItem.taxable,
          quantity: registryItem.quantity,
          discountType: registryItem.discountType,
          discount: registryItem.discount,
        }),
      )
    onAdd(selectedItems)
    setRowSelection({})
    seenItemsById.current.clear()
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 px-2 pt-3 pb-2 sm:px-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search registry items…"
            value={searchInput}
            onChange={(changeEvent) => setSearchInput(changeEvent.target.value)}
            aria-label="Search registry items"
            autoFocus
          />
        </div>
        <ColumnVisibilityMenu
          columns={COLUMN_SPECS}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-2 py-2 sm:px-4">
        <DataTable
          columns={registryColumns}
          data={registryItems}
          isLoading={isLoading}
          emptyMessage={
            debouncedSearch ? 'No items match your search.' : 'No registry items found.'
          }
          className="min-h-0 flex-1"
          stickyHeader
          getRowId={(registryItem) => registryItem.id}
          enableSelection
          clearSelectionOnDataChange={false}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          onRowClick={handleRowClick}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
          enableColumnPinning
          pinnedColumnId={pinnedColumnId}
          onPinnedColumnChange={onPinnedColumnChange}
        />
      </div>

      <div className="flex flex-col-reverse gap-2 border-t px-2 py-3 sm:flex-row sm:justify-end sm:px-4">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleAdd}
          disabled={selectedIds.length === 0}
        >
          {selectedIds.length > 0
            ? `Add selected (${selectedIds.length})`
            : 'Add selected'}
        </Button>
      </div>
    </div>
  )
}
